import { FC } from 'react';
import type { ControllerRenderProps } from 'react-hook-form';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormCountrySelectNova } from '@hub/features/forms';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { formCountryValidation } from '@monorepo/client-common';
import { DesignIcon } from '@monorepo/nova-icons';
import { designTokens } from '@monorepo/nova-ui';
import { RegisterRequest } from '@monorepo/types';
import {
  StyledButtonRow,
  StyledEmailIconWrapper,
  StyledPillButton,
  StyledStepContainer,
} from './quick-purchase-modal.styles';

interface CountryFormValues {
  country: RegisterRequest['country'];
}

interface QuickPurchaseCountryStepProps {
  countryForm: UseFormReturn<CountryFormValues>;
  isLoading: boolean;
  onSubmit: (values: CountryFormValues) => Promise<void>;
}

export const QuickPurchaseCountryStep: FC<QuickPurchaseCountryStepProps> = ({
  countryForm,
  isLoading,
  onSubmit,
}) => {
  const { t } = useTranslation(['quick-purchase']);

  return (
    <StyledStepContainer gap={6}>
      <Stack gap={1}>
        <StyledEmailIconWrapper>
          <DesignIcon
            iconName="MouseClick"
            useMuiSvgIcon={false}
            style={{ transform: 'scaleX(-1)' }}
          />
        </StyledEmailIconWrapper>
        <Typography
          variant="h3"
          fontWeight={designTokens.typography.fontWeights.bold}
        >
          {t('modal.country_step.title')}
        </Typography>
      </Stack>
      <Typography variant="body1" color="text.regular">
        {t('modal.country_step.subtitle')}
      </Typography>

      <Controller
        name="country"
        control={countryForm.control}
        rules={formCountryValidation()}
        render={({ field, fieldState }) => (
          <FormCountrySelectNova
            field={
              field as unknown as ControllerRenderProps<
                RegisterRequest,
                'country'
              >
            }
            error={fieldState.error}
          />
        )}
      />

      <StyledButtonRow>
        <StyledPillButton
          variant="primary"
          disabled={isLoading}
          onClick={countryForm.handleSubmit(onSubmit)}
        >
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            t('modal.country_step.continue_button')
          )}
        </StyledPillButton>
      </StyledButtonRow>
    </StyledStepContainer>
  );
};
